import {
	IRegEdMetadataField,
	IRegEdMetadataForm,
	IRegEdMetadataOption,
	RegEdFieldType,
} from '@Queries/RegEdCompliance/types';
import * as Api from '@ViewModels';
import { computeVisibleFields } from '../../../../surveys/containers/SurveyResponses/shared/hooks/useConditionalVisibility';

// `Object.values` on a string enum yields its members in declaration order, so the array index
// matches the integer ordinal an endpoint may send in place of the string member name.
const fieldTypesByOrdinal = Object.values(RegEdFieldType);

export const normalizeFieldType = (raw: unknown): RegEdFieldType | undefined => {
	if (typeof raw === 'string') {
		return (fieldTypesByOrdinal as string[]).includes(raw) ? (raw as RegEdFieldType) : undefined;
	}
	if (typeof raw === 'number') {
		return fieldTypesByOrdinal[raw];
	}
	return undefined;
};

// Options frequently omit `externalId`, and imported `visibleWhen.fieldValue` rules reference the
// option LABEL — so the label is the reliable answer value (`externalId`, when present, equals it).
export const regEdOptionValue = (option: IRegEdMetadataOption): string => option.externalId ?? option.label ?? '';

export const orderedActiveFields = (schema: IRegEdMetadataForm | null | undefined): IRegEdMetadataField[] => {
	const fields = schema?.fields ?? [];
	return fields
		.filter(f => f.isActive !== false && !!f.id)
		.slice()
		.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

const hasEvaluableType = (visibleWhen: unknown): visibleWhen is Api.QuestionVisibility =>
	!!visibleWhen && typeof (visibleWhen as { _type?: unknown })._type === 'string';

// Forward `visibleWhen` only when it carries a recognizable `_type` discriminator — anything
// without one is treated as unconditionally visible rather than silently hidden.
export const toSurveyForm = (fields: IRegEdMetadataField[]): Api.SurveyForm => ({
	surveyParts: fields.map(field => {
		const part: Api.SurveyQuestion = {
			id: field.id!,
			_type: 'ShortAnswerQuestion',
			fieldId: field.id!,
			visibleWhen: hasEvaluableType(field.visibleWhen) ? field.visibleWhen : null,
		};
		return part;
	}),
});

// Multi-select answers become comma-separated so the visibility hook's `includes` check works.
export const toFormValues = (answers: Record<string, unknown>): Record<string, string> => {
	const out: Record<string, string> = {};
	for (const [key, value] of Object.entries(answers)) {
		if (Array.isArray(value)) {
			out[key] = value.map(String).join(',');
		} else if (value != null && typeof value !== 'object') {
			out[key] = String(value);
		}
	}
	return out;
};

const isAnswerEmpty = (value: unknown): boolean =>
	value == null || value === '' || (Array.isArray(value) && value.length === 0);

// A filing must never carry answers the advisor can no longer see or retract; cascades to a
// fixpoint since clearing a controlling field can hide its dependents.
export const stripHiddenFieldAnswers = (
	schema: IRegEdMetadataForm | null | undefined,
	answers: Record<string, unknown>
): Record<string, unknown> => {
	const fields = orderedActiveFields(schema);
	const surveyForm = toSurveyForm(fields);
	if (!surveyForm.surveyParts.length) {
		return answers;
	}
	const governedFieldIds = new Set(fields.map(field => field.id!));
	let current = answers;
	for (let pass = 0; pass <= governedFieldIds.size; pass++) {
		const visible = computeVisibleFields(surveyForm, toFormValues(current));
		let changed = false;
		const next: Record<string, unknown> = {};
		for (const [key, value] of Object.entries(current)) {
			if (governedFieldIds.has(key) && !visible.has(key)) {
				changed = true;
				continue;
			}
			next[key] = value;
		}
		if (!changed) {
			return current;
		}
		current = next;
	}
	return current;
};

// Required-field validation lives client-side — the API only warns on unmapped keys rather than rejecting.
export const findMissingRequiredFieldIds = (
	schema: IRegEdMetadataForm | null | undefined,
	answers: Record<string, unknown>
): string[] => {
	const fields = orderedActiveFields(schema);
	const surveyForm = toSurveyForm(fields);
	const visible = surveyForm.surveyParts.length ? computeVisibleFields(surveyForm, toFormValues(answers)) : undefined;
	return fields
		.filter(field => {
			if (!field.isRequired || !field.id) {
				return false;
			}
			if (normalizeFieldType(field.fieldType) === RegEdFieldType.Label) {
				return false;
			}
			if (visible && !visible.has(field.id)) {
				return false;
			}
			return isAnswerEmpty(answers[field.id]);
		})
		.map(field => field.id!);
};
